<template>
  <div class="size-guide-content">
    <size-chart
      :categories="categories"
      :context="sizeGuideContext"
      :general-category="generalCategory"
      :locales="locales"
      :product-type="productType"
      :section-id="sectionId"
      :unit-types="unitTypes"
    />

    <div
      v-if="accordionBlocks.length"
      class="size-guide-content__accordions"
    >
      <div class="accordion">
        <accordion-item
          v-for="(block, index) in accordionBlocks"
          :key="index"
          :title="block.title"
          :html="block.html"
        >
          <template #content>
            <div
              :class="{ 'is-halved': block.text && getContentImage(block) }"
              class="accordion-item__content"
            >
              <div
                v-if="block.text"
                class="accordion-item__text"
                v-html="block.text"
              />

              <responsive-image
                v-if="getContentImage(block)"
                class="accordion-item__image"
                :image="getContentImage(block)"
                min-max="400-1400"
              />
            </div>
          </template>
        </accordion-item>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Component: Size Guide
 * ------------------------------------------------------------------------------
 * - Size Guide visual elements
 * - Rendered inside a component rather than a section because the content
 *   may appear inline or within an overlay depending on context.
 * @param {Array} [accordionBlocks] - Formatted block objects which contain title and contents for accordions.
 * @param {String} context - Location of the rendered Size Guide.
 * @param {Array} [categories] - Categories blocks which contain title and synonyms strings.
 * @param {Boolean} [isMobile] - Mobile/desktop state, from Pinia
 * @param {String} [generalCategory] - General category string to use on size-guide page templates.
 * @param {String} [locales] - A string containing a list of locales separated by a comma.
 * @param {String} [sectionId] - Unique section ID, from Liquid.
 * @param {String} [unitTypes] - A string containing a list of unit types separated by a comma.
 * @param {String} [productType] - The type of current product used to predefine category value.
 */
import AccordionItem from '~components/accordion-item/accordion-item'
import SizeChart from '~components/size-guide/size-chart/size-chart'
import ResponsiveImage from '~components/responsive-image/responsive-image'

export default {
  name: 'SizeGuideContent',

  components: {
    AccordionItem,
    ResponsiveImage,
    SizeChart,
  },

  props: {
    accordionBlocks: {
      type: [Boolean, Array],
      default: false,
    },
    context: {
      type: String,
      default: '',
    },
    categories: {
      type: Array,
      default: () => [],
    },
    generalCategory: {
      type: String,
      default: 'General',
    },
    isMobile: {
      type: Boolean,
      default: true,
    },
    locales: {
      type: String,
      default: 'UK, EU, US',
    },
    sectionId: {
      type: String,
      default: '',
    },
    unitTypes: {
      type: String,
      default: 'cm, in',
    },
    productType: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      showOverlay: false,
    }
  },

  computed: {

    /**
     * Calculate the 'Close' button text.
     * @returns {String}
     */
    closeOverlayText() {
      return mint.strings.accessibility.close
    },

    /**
     * Calculates context for size-chart table depending on the template.
     *
     * @returns {string} - either `product` or `general`
     */
    sizeGuideContext() {
      return mint.request.pageType === 'product' ? 'product' : 'general'
    },
  },

  methods: {

    /**
     * Calculates content image based on current device.
     * @param {object} block - formatted section block with content data.
     * @param {object} [block.image] - Formatted image object.
     * @param {object} [block.image_desktop] - Formatted image object for desktop.
     *
     * @returns {boolean|object}
     */
    getContentImage(block) {
      if (!block || (!block.image && !block.image_desktop)) {
        return false
      }

      if (!this.isMobile && block.image_desktop) {
        return block.image_desktop
      }

      return block.image
    },
  },
}
</script>
